import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "./Header"
const UNIT_OPTIONS = ["inches", "feet", "cm", "meters"];
const TILE_COLORS  = ["#f0e8dc", "#e8ddd0", "#ede3d8", "#e4d8cc"];
const CUT_COLOR    = "#f4a56a";
const GROUT_COLOR  = "#9a8878";
const MIN_VISIBLE  = 0.25;

function toInches(v, unit) {
  if (!isFinite(v) || v <= 0) return 0;
  switch (unit) {
    case "feet":   return v * 12;
    case "cm":     return v / 2.54;
    case "meters": return v * 39.3701;
    default:       return v;
  }
}

function fmt(inches) {
  if (!isFinite(inches) || inches < 0) return '0"';
  const n32 = Math.round(inches * 32);
  const whole = Math.floor(n32 / 32), num = n32 % 32;
  if (num === 0) return `${whole}″`;
  function gcd(a, b) { return b === 0 ? a : gcd(b, a % b); }
  const g = gcd(num, 32);
  return whole === 0 ? `${num/g}/${32/g}″` : `${whole} ${num/g}/${32/g}″`;
}
function pct(v, total) { return Math.min(100, v / total * 100).toFixed(1); }

function parseFraction(s) {
  if (!s || !s.trim()) return NaN;
  s = s.trim().replace(/["″]/g, '').trim();
  const dec = parseFloat(s);
  if (!isNaN(dec) && String(dec) === s) return dec;
  const wf = s.match(/^(\d+(?:\.\d+)?)\s*[-\s]\s*(\d+)\s*\/\s*(\d+)$/);
  if (wf) { const d = parseInt(wf[3]); return d ? parseFloat(wf[1]) + parseInt(wf[2])/d : NaN; }
  const f = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (f) { const d = parseInt(f[2]); return d ? parseInt(f[1])/d : NaN; }
  return isNaN(dec) ? NaN : dec;
}
function isOk(v) { return isFinite(v) && v > 0; }

function solveCorners(topW, leftH, rightH, bottomW) {
  if (!isOk(topW)||!isOk(leftH)||!isOk(rightH)||!isOk(bottomW)) return null;
  const K = (bottomW*bottomW - rightH*rightH + topW*topW - leftH*leftH) / (2*topW);
  const A = leftH/topW, M = K-topW;
  const a = A*A+1, b = 2*M*A, c = M*M - rightH*rightH;
  const disc = b*b - 4*a*c;
  if (disc < 0) return null;
  const sq = Math.sqrt(disc);
  for (const s of [1,-1]) {
    const by = (-b + s*sq)/(2*a), bx = K + A*by;
    if (by > 0 && bx >= 0) {
      const d1 = Math.sqrt(bx*bx + (by-leftH)**2);
      const d2 = Math.sqrt((bx-topW)**2 + by**2);
      if (Math.abs(d1-bottomW)<0.01 && Math.abs(d2-rightH)<0.01)
        return { TL:[0,0], TR:[topW,0], BL:[0,leftH], BR:[bx,by] };
    }
  }
  return null;
}

function wallXatY(p, q, y) {
  const dy = q[1]-p[1];
  if (Math.abs(dy) < 1e-9) return null;
  const t = (y-p[1])/dy;
  if (t < -1e-6 || t > 1+1e-6) return null;
  return p[0] + t*(q[0]-p[0]);
}

function bottomWallY(x, BL, BR) {
  const dx = BR[0]-BL[0];
  if (Math.abs(dx) < 1e-9) return Math.min(BL[1], BR[1]);
  const t = Math.max(0, Math.min(1, (x-BL[0])/dx));
  return BL[1] + t*(BR[1]-BL[1]);
}

function installedDims(xGrid, yGrid, tileL, tileB, corners) {
  const { TL, TR, BR, BL } = corners;

  const lTop = wallXatY(TL, BL, yGrid)       ?? 0;
  const lBot = wallXatY(TL, BL, yGrid+tileB) ?? 0;
  const leftBound = Math.max(lTop, lBot, 0);

  const rFallback = (y) => y > BR[1] ? BR[0] : TR[0];
  const rTop = wallXatY(TR, BR, yGrid)       ?? rFallback(yGrid);
  const rBot = wallXatY(TR, BR, yGrid+tileB) ?? rFallback(yGrid+tileB);
  const rInstalled = Math.min(xGrid + tileL, Math.min(rTop, rBot));

  const xL = Math.max(xGrid, leftBound);
  if (rInstalled - xL < 1e-6) return null;

  const iW = rInstalled - xL;

  const xMid = xL + iW / 2;
  const yBotWall = bottomWallY(xMid, BL, BR);
  const yT  = Math.max(yGrid, 0);
  const yBi = Math.min(yGrid + tileB, yBotWall);
  if (yBi - yT < 1e-6) return null;

  const iH = yBi - yT;

  const renderW = (xGrid < -1e-6) ? iW : tileL;
  const renderH = (yGrid < -1e-6) ? iH : tileB;

  const iWtop = Math.min(xGrid + tileL, rTop) - xL;
  const iWbot = Math.min(xGrid + tileL, rBot) - xL;
  const yBotLeft  = bottomWallY(xL,        BL, BR);
  const yBotRight = bottomWallY(xL + iW,   BL, BR);
  const iHleft  = Math.min(yGrid + tileB, yBotLeft)  - yT;
  const iHright = Math.min(yGrid + tileB, yBotRight) - yT;
  return { iW, iH, renderW, renderH, xL, yT, iWtop, iWbot, iHleft, iHright, rTop, rBot };
}

function calcLayout(topW, leftH, rightH, bottomW, tileL, tileB, gapIn, shiftL, shiftB, offset, dir) {
  if (!isOk(topW)||!isOk(leftH)||!isOk(rightH)||!isOk(bottomW)||
      !isOk(tileL)||!isOk(tileB)||!isOk(gapIn))
    return { tiles:[], totalTiles:0, cutList:[], totalCuts:0, corners:null };

  const corners = solveCorners(topW, leftH, rightH, bottomW);
  if (!corners) return { tiles:[], totalTiles:0, cutList:[], totalCuts:0, corners:null };

  if (dir === 'tb') { [tileL, tileB] = [tileB, tileL]; [shiftL, shiftB] = [shiftB, shiftL]; }

  const pitchL = tileL + gapIn, pitchB = tileB + gapIn;
  const maxH = Math.max(corners.BL[1], corners.BR[1]);

  shiftL = ((shiftL % pitchL) + pitchL) % pitchL;
  shiftB = ((shiftB % pitchB) + pitchB) % pitchB;

  const x0 = shiftL > 1e-6 ? -(pitchL - shiftL) : 0;
  const y0 = shiftB > 1e-6 ? -(pitchB - shiftB) : 0;

  const tiles = [];
  const cutMap = new Map();
  function r32(v) { return Math.round(v * 32) / 32; }

  const maxW = Math.max(corners.TR[0], corners.BR[0]);

  function addCutToMap(iW, iH) {
    const wV=r32(iW), hV=r32(iH);
    const wC=iW<tileL-1e-6, hC=iH<tileB-1e-6;
    const label = wC&&hC ? 'Corner cut' : wC ? 'Width cut' : 'Height cut';
    const key = `${wV}:${hV}:${label}`;
    const ex = cutMap.get(key);
    cutMap.set(key, ex
      ? { ...ex, count: ex.count+1 }
      : { id:key, label, w:wV, h:hV, cutsRequired:(wC?1:0)+(hC?1:0), count:1 });
  }

  if (dir === 'tb') {
    let ci = 0;
    for (let xGrid = x0; xGrid < maxW + pitchL; xGrid += pitchL, ci++) {
      if (xGrid + tileL < 1e-6) continue;
      if (xGrid > maxW + pitchL * 2) break;
      const colOff = ((offset * tileB * ci) % pitchB + pitchB) % pitchB;
      const yStart = y0 - colOff;
      let ri = 0;
      const colStart = tiles.length;
      for (let yGrid = yStart; yGrid < maxH + 1e-6; yGrid += pitchB, ri++) {
        if (yGrid + tileB < 1e-6) continue;
        const dims = installedDims(xGrid, yGrid, tileL, tileB, corners);
        if (!dims) continue;
        const { iW, iH, xL, yT } = dims;
        if (iW < MIN_VISIBLE || iH < MIN_VISIBLE) continue;
        const isCut = iW < tileL - 1e-6 || iH < tileB - 1e-6;
        tiles.push({ xL, yT, iW, iH, renderW: dims.renderW, renderH: dims.renderH, isCut, ri, ci, iWtop: dims.iWtop, iWbot: dims.iWbot, iHleft: dims.iHleft, iHright: dims.iHright });
        if (isCut) addCutToMap(iW, iH);
      }
      if (tiles.length > colStart) {
        const last = tiles[tiles.length - 1];
        const rFbRow = (y) => y > corners.BR[1] ? corners.BR[0] : corners.TR[0];
        const rTopRow = wallXatY(corners.TR, corners.BR, last.yT) ?? rFbRow(last.yT);
        const neededW = rTopRow - last.xL;
        if (neededW > last.renderW) last.renderW = neededW;
      }
    }
  } else {
    let ri = 0;
    for (let yGrid = y0; yGrid < maxH + 1e-6; yGrid += pitchB, ri++) {
      if (yGrid + tileB < 1e-6) continue;
      const rowOff = ((offset * tileL * ri) % pitchL + pitchL) % pitchL;
      const xStart = x0 - rowOff;
      let ci = 0;
      const rowStart = tiles.length;
      for (let xGrid = xStart; xGrid < maxW + pitchL; xGrid += pitchL, ci++) {
        if (xGrid + tileL < 1e-6) continue;
        if (xGrid > maxW + pitchL * 2) break;
        const dims = installedDims(xGrid, yGrid, tileL, tileB, corners);
        if (!dims) continue;
        const { iW, iH, xL, yT } = dims;
        if (iW < MIN_VISIBLE || iH < MIN_VISIBLE) continue;
        const isCut = iW < tileL - 1e-6 || iH < tileB - 1e-6;
        tiles.push({ xL, yT, iW, iH, renderW: dims.renderW, renderH: dims.renderH, isCut, ri, ci, iWtop: dims.iWtop, iWbot: dims.iWbot, iHleft: dims.iHleft, iHright: dims.iHright });
        if (isCut) addCutToMap(iW, iH);
      }
      if (tiles.length > rowStart) {
        const last = tiles[tiles.length - 1];
        const rFbRow = (y) => y > corners.BR[1] ? corners.BR[0] : corners.TR[0];
        const rTopRow = wallXatY(corners.TR, corners.BR, last.yT) ?? rFbRow(last.yT);
        const neededW = rTopRow - last.xL;
        if (neededW > last.renderW) last.renderW = neededW;
      }
    }
  }

  const cutList = Array.from(cutMap.values());
  const totalCuts = cutList.reduce((s,c) => s + c.count*c.cutsRequired, 0);
  return { tiles, totalTiles: tiles.length, cutList, totalCuts, corners, tileL, tileB };
}

function computeScale(topW, leftH, rightH, bottomW) {
  const corners = solveCorners(topW, leftH, rightH, bottomW);
  const maxH = corners ? Math.max(corners.BL[1], corners.BR[1]) : Math.max(leftH, rightH);
  const maxW = corners ? Math.max(corners.TR[0], corners.BR[0]) : topW;
  return Math.min(520 / maxW, 440 / maxH);
}

function TilePreview({ topW, leftH, rightH, bottomW, tileL, tileB, gapIn, offset, startMode, scale, onLayoutChange, reusePairs, layoutDir, externalShiftL, externalShiftB, onShiftChange, skipResetRef }) {
  const shiftRef = useRef({ L: externalShiftL ?? 0, B: externalShiftB ?? 0 });
  // Fully controlled — shiftL/shiftB live in parent, mirrored here via ref for drag perf
  const shiftL = externalShiftL ?? 0;
  const shiftB = externalShiftB ?? 0;
  const [hovered, setHovered] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [ctrlCursor, setCtrlCursor] = useState(null);
  const drag = useRef(null);
  const svgRef = useRef(null);
  const pitchL = tileL + gapIn, pitchB = tileB + gapIn;
  const [ctrlX, setCtrlX] = useState(null);
  const [ctrlY, setCtrlY] = useState(null);
  const ctrlDrag = useRef(null);
  const [snapNear, setSnapNear] = useState({ x: false, y: false }); // true when near a grout line

  function applyShift(L, B) {
    L = ((L % pitchL) + pitchL) % pitchL;
    B = ((B % pitchB) + pitchB) % pitchB;
    shiftRef.current = { L, B };
    onShiftChange?.(L, B);
  }

  function applyShiftClamped(L, B) {
    L = Math.max(0, Math.min(pitchL, L));
    B = Math.max(0, Math.min(pitchB, B));
    shiftRef.current = { L, B };
    onShiftChange?.(L, B);
  }

  // Keep ref in sync with controlled values
  useEffect(() => {
    shiftRef.current = { L: shiftL, B: shiftB };
  }, [shiftL, shiftB]);

  useEffect(() => {
    if (skipResetRef?.current) return;
    shiftRef.current = { L: 0, B: 0 };
    onShiftChange?.(0, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topW, leftH, rightH, bottomW, tileL, tileB, gapIn, offset, startMode]);

  useEffect(() => { setCtrlX(null); setCtrlY(null); },
    [topW, leftH, rightH, bottomW]);

  const layout = useMemo(() => {
    const corners0 = solveCorners(topW, leftH, rightH, bottomW);
    const maxH0 = corners0 ? Math.max(corners0.BL[1], corners0.BR[1]) : Math.max(leftH, rightH);
    const wrap = (v, p) => ((v%p)+p)%p;
    let bL = 0, bB = 0;
    if      (startMode === 'right')  bL = wrap(topW, pitchL);
    else if (startMode === 'bottom') bB = wrap(maxH0, pitchB);
    else if (startMode === 'center') {
      bL = ((topW / 2 + gapIn / 2) % pitchL + pitchL) % pitchL;
    }
    return calcLayout(topW, leftH, rightH, bottomW, tileL, tileB, gapIn,
      wrap(bL + shiftL, pitchL), wrap(bB + shiftB, pitchB), offset, layoutDir);
  }, [topW, leftH, rightH, bottomW, tileL, tileB, gapIn, shiftL, shiftB, offset, startMode, layoutDir]);

  useEffect(() => { onLayoutChange(layout); }, [layout, onLayoutChange]);

  const corners = layout.corners;
  const maxH = corners ? Math.max(corners.BL[1], corners.BR[1]) : Math.max(leftH, rightH);
  const maxW = corners ? Math.max(corners.TR[0], corners.BR[0]) : topW;
  const W = maxW * scale, H = maxH * scale;
  const PAD = 6;

  const polyPts = corners
    ? [corners.TL, corners.TR, corners.BR, corners.BL]
        .map(p => `${p[0]*scale},${p[1]*scale}`).join(' ')
    : `0,0 ${W},0 ${W},${rightH*scale} 0,${leftH*scale}`;

  function getSVGPoint(e) {
    if (!svgRef.current) return null;
    const pt = svgRef.current.createSVGPoint();
    pt.x = e.touches?.[0]?.clientX ?? e.clientX;
    pt.y = e.touches?.[0]?.clientY ?? e.clientY;
    return pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
  }

  function onPointerDown(e) {
    e.preventDefault();
    const cx0 = e.touches?.[0]?.clientX ?? e.clientX;
    const cy0 = e.touches?.[0]?.clientY ?? e.clientY;

    const svgP = getSVGPoint(e);
    if (svgP) {
      const px = svgP.x, py = svgP.y;
      const HIT = 8;
      const effCtrlXpd = (ctrlX ?? maxW/2) * scale;
      const effCtrlYpd = (ctrlY ?? maxH/2) * scale;
      const nearV = Math.abs(px - effCtrlXpd) <= HIT;
      const nearH = Math.abs(py - effCtrlYpd) <= HIT;

      if (nearV || nearH) {
        const axis = (nearV && nearH)
          ? (Math.abs(px - effCtrlXpd) < Math.abs(py - effCtrlYpd) ? 'x' : 'y')
          : nearV ? 'x' : 'y';
        ctrlDrag.current = { axis };
        const moveCtrl = (mcx, mcy) => {
          if (!ctrlDrag.current) return;
          const svgM = (() => {
            const pt = svgRef.current?.createSVGPoint();
            if (!pt) return null;
            pt.x = mcx; pt.y = mcy;
            return pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
          })();
          if (!svgM) return;
          const snapCheck = (valIn, pitch, shift) => {
            const threshold = 0.25 * pitch;
            const offset0 = shift > 1e-6 ? pitch - shift : 0;
            const nearest = Math.round((valIn - offset0) / pitch) * pitch + offset0;
            return Math.abs(valIn - nearest) <= threshold;
          };
          if (ctrlDrag.current.axis === 'x') {
            const v = Math.max(0, Math.min(maxW, svgM.x / scale));
            setCtrlX(v);
            setSnapNear(s => ({ ...s, x: snapCheck(v, pitchL, shiftRef.current.L) }));
          } else {
            const v = Math.max(0, Math.min(maxH, svgM.y / scale));
            setCtrlY(v);
            setSnapNear(s => ({ ...s, y: snapCheck(v, pitchB, shiftRef.current.B) }));
          }
        };
        const mmC = ev => moveCtrl(ev.clientX, ev.clientY);
        const tmC = ev => { ev.preventDefault(); moveCtrl(ev.touches[0].clientX, ev.touches[0].clientY); };
        const snapToGrout = (valIn, pitch, shift) => {
          // Grout lines are at: x0 + n*pitch where x0 = shift>0 ? -(pitch-shift) : 0
          // Equivalently, grout line positions mod pitch = (pitch - shift) % pitch
          const threshold = 0.25 * pitch;
          const offset0 = shift > 1e-6 ? pitch - shift : 0;
          // nearest grout line = round((valIn - offset0) / pitch) * pitch + offset0
          const nearest = Math.round((valIn - offset0) / pitch) * pitch + offset0;
          return Math.abs(valIn - nearest) <= threshold ? nearest : valIn;
        };
        const endC = () => {
          // Snap to nearest grout line on release if within threshold
          if (ctrlDrag.current?.axis === 'x') {
            setCtrlX(prev => {
              const v = prev ?? maxW/2;
              return Math.max(0, Math.min(maxW, snapToGrout(v, pitchL, shiftRef.current.L)));
            });
          } else if (ctrlDrag.current?.axis === 'y') {
            setCtrlY(prev => {
              const v = prev ?? maxH/2;
              return Math.max(0, Math.min(maxH, snapToGrout(v, pitchB, shiftRef.current.B)));
            });
          }
          ctrlDrag.current = null;
          setSnapNear({ x: false, y: false });
          window.removeEventListener('mousemove', mmC);
          window.removeEventListener('mouseup', endC);
          window.removeEventListener('touchmove', tmC);
          window.removeEventListener('touchend', endC);
        };
        window.addEventListener('mousemove', mmC);
        window.addEventListener('mouseup', endC);
        window.addEventListener('touchmove', tmC, { passive: false });
        window.addEventListener('touchend', endC);
        return;
      }
    }

    drag.current = { sx: cx0, sy: cy0, initL: shiftRef.current.L, initB: shiftRef.current.B, moved: false };
    setIsDragging(true); setHovered(null);
    const move = (cx, cy) => {
      if (!drag.current) return;
      const dx = cx - drag.current.sx, dy = cy - drag.current.sy;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) drag.current.moved = true;
      applyShift(drag.current.initL + dx/scale, drag.current.initB + dy/scale);
    };
    const mm = ev => move(ev.clientX, ev.clientY);
    const tm = ev => { ev.preventDefault(); move(ev.touches[0].clientX, ev.touches[0].clientY); };
    const end = () => {
      drag.current = null; setIsDragging(false);
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchmove', tm);
      window.removeEventListener('touchend', end);
    };
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', end);
    window.addEventListener('touchmove', tm, { passive:false });
    window.addEventListener('touchend', end);
  }

  function onSVGMouseMove(e) {
    if (isDragging) return;
    const p = getSVGPoint(e);
    if (!p) return;
    const xi = p.x/scale, yi = p.y/scale;
    const t = layout.tiles.find(t => xi>=t.xL && xi<=t.xL+t.iW && yi>=t.yT && yi<=t.yT+t.iH);
    setHovered(t ?? null);
    setCtrlCursor(getCtrlCursor(e));
  }

  function getCtrlCursor(e) {
    const p = getSVGPoint(e);
    if (!p || isDragging) return null;
    const HIT = 8;
    const effCtrlXgc = (ctrlX ?? maxW/2) * scale;
    const effCtrlYgc = (ctrlY ?? maxH/2) * scale;
    const nearV = Math.abs(p.x - effCtrlXgc) <= HIT;
    const nearH = Math.abs(p.y - effCtrlYgc) <= HIT;
    if (nearV && nearH) return 'move';
    if (nearV) return 'ew-resize';
    if (nearH) return 'ns-resize';
    return null;
  }

  useEffect(() => {
    const onKey = e => {
      if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key)) return;
      if (document.activeElement !== svgRef.current) return;
      e.preventDefault();
      const step = (e.shiftKey ? 5 : 1) / scale;
      const {L, B} = shiftRef.current;
      applyShift(
        e.key==='ArrowLeft'?L-step:e.key==='ArrowRight'?L+step:L,
        e.key==='ArrowUp'?B-step:e.key==='ArrowDown'?B+step:B);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [scale]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('touchstart', onPointerDown, { passive: false });
    return () => el.removeEventListener('touchstart', onPointerDown);
  });

  // Slider values in inches (0 → pitchL/pitchB), step 1/32"
  const sliderH = Math.min(pitchL, shiftL);
  const sliderV = Math.min(pitchB, shiftB);
  const hasShift = shiftL > gapIn || shiftB > gapIn;

  const hoverEl = useMemo(() => {
    if (!hovered || isDragging) return null;
    const t = hovered;
    const x=t.xL*scale, y=t.yT*scale, w=t.iW*scale, h=t.iH*scale;
    const eTileL = layout.tileL ?? tileL;
    const eTileB = layout.tileB ?? tileB;
    const wC=t.iW<eTileL-1e-6, hC=t.iH<eTileB-1e-6;
    const c = layout.corners;

    const iWtop = t.iWtop ?? t.iW, iWbot = t.iWbot ?? t.iW;
    const iHleft = t.iHleft ?? t.iH, iHright = t.iHright ?? t.iH;
    // Use max for slant cuts — simpler and unambiguous
    const displayW = wC ? Math.max(iWtop, iWbot) : eTileL;
    const displayH = hC ? Math.max(iHleft, iHright) : eTileB;
    const dimW = fmt(displayW);
    const dimH = fmt(displayH);

    const distLeft  = t.xL;
    const distTop   = t.yT;
    const rFb = (y) => c ? (y > c.BR[1] ? c.BR[0] : c.TR[0]) : 0;
    const rWallMid  = c ? (wallXatY(c.TR, c.BR, t.yT + t.iH/2) ?? rFb(t.yT + t.iH/2)) : 0;
    const distRight = Math.max(0, rWallMid - (t.xL + t.iW));
    const bWallMid  = c ? bottomWallY(t.xL + t.iW/2, c.BL, c.BR) : 0;
    const distBot   = Math.max(0, bWallMid - (t.yT + t.iH));

    const effCtrlX = ctrlX ?? maxW/2;
    const effCtrlY = ctrlY ?? maxH/2;
    const ctrlVinTile = effCtrlX >= t.xL - 1e-6 && effCtrlX <= t.xL + t.iW + 1e-6;
    const ctrlHinTile = effCtrlY >= t.yT - 1e-6 && effCtrlY <= t.yT + t.iH + 1e-6;

    const lines = [
      `${dimW} × ${dimH}`,
      wC&&hC ? 'Corner — 2 cuts'
        : wC ? `Width cut — ${pct(displayW,eTileL)}% of ${fmt(eTileL)}`
        : hC ? `Height cut — ${pct(displayH,eTileB)}% of ${fmt(eTileB)}`
        : 'Full tile',
      `← ${fmt(distLeft)}  → ${fmt(distRight)}`,
      `↑ ${fmt(distTop)}  ↓ ${fmt(distBot)}`,
      ...(ctrlVinTile ? [`V-line: ${fmt(effCtrlX - t.xL)} from tile left`] : []),
      ...(ctrlHinTile ? [`H-line: ${fmt(effCtrlY - t.yT)} from tile top`] : []),
    ];
    const TW=200, lH=16, TH=lines.length*lH+12, pad=8;
    let tx=x+w/2-TW/2, ty=y-TH-6;
    if(ty<pad) ty=y+h+6;
    if(tx+TW>W-pad) tx=W-TW-pad;
    tx=Math.max(pad,tx);
    const tickVx = effCtrlX * scale;
    const tickHy = effCtrlY * scale;

    return (
      <g style={{pointerEvents:'none'}}>
        <rect x={x} y={y} width={w} height={h}
          fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.8)" strokeWidth={1.5} rx={1}/>
        {ctrlVinTile && (
          <line x1={tickVx} y1={y} x2={tickVx} y2={y+h}
            stroke="#2980b9" strokeWidth={2} opacity={0.9}/>
        )}
        {ctrlHinTile && (
          <line x1={x} y1={tickHy} x2={x+w} y2={tickHy}
            stroke="#27ae60" strokeWidth={2} opacity={0.9}/>
        )}
        <rect x={tx} y={ty} width={TW} height={TH} rx={4} fill="#1a1410" opacity={0.92}/>
        {lines.map((l,i)=>(
          <text key={i} x={tx+8} y={ty+14+i*lH}
            fontSize={i===0?10:i<2?9:8.5}
            fontWeight={i===0?700:400}
            fill={i===0?(t.isCut?CUT_COLOR:'#f5f0e8'):i===1?'#c9b8a8':i<4?'#7a9a8a':i===4?'#7ab8e8':'#7ae8a8'}
            fontFamily="monospace">{l}</text>
        ))}
      </g>
    );
  }, [hovered, isDragging, scale, tileL, tileB, W, layout, ctrlX, ctrlY, maxW, maxH]);

  return (
    <div>
      <svg ref={svgRef}
        viewBox={`${-PAD} ${-PAD} ${W+PAD*2} ${H+PAD*2}`}
        width="100%" tabIndex={0}
        style={{display:'block',borderRadius:6,border:'2px solid #d4c8bc',
          boxShadow:'4px 4px 0 #d4c8bc',userSelect:'none',touchAction:'none',
          background:'#b0a090',outline:'none',
          cursor:ctrlDrag.current?'grabbing':ctrlCursor||(isDragging?'grabbing':'default')}}
        onMouseMove={onSVGMouseMove}
        onMouseLeave={()=>setHovered(null)}
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
>
        <defs>
          <clipPath id="room-clip"><polygon points={polyPts}/></clipPath>
        </defs>
        <polygon points={polyPts} fill={GROUT_COLOR}/>
        <g clipPath="url(#room-clip)">
          {layout.tiles.map((t,i)=>{
            const rw = (t.renderW ?? tileL) * scale;
            const rh = (t.renderH ?? tileB) * scale;
            const x = t.xL * scale, y = t.yT * scale;
            const fill = t.isCut ? CUT_COLOR : TILE_COLORS[(t.ri+t.ci)%TILE_COLORS.length];
            return (
              <g key={i}>
                <rect x={x} y={y} width={rw} height={rh} fill={fill} stroke="none"/>
              </g>
            );
          })}
          {hoverEl}
          {startMode === 'center' && (() => {
            const clX = (topW / 2) * scale;
            const dashLen = Math.max(4, H * 0.025);
            return (
              <g style={{pointerEvents:'none'}}>
                <line x1={clX} y1={0} x2={clX} y2={H}
                  stroke="#c0392b" strokeWidth={2}
                  strokeDasharray={`${dashLen} ${dashLen * 0.6}`}
                  opacity={0.85}/>
                <rect x={clX - 14} y={4} width={28} height={16} rx={3}
                  fill="#c0392b" opacity={0.9}/>
                <text x={clX} y={16} textAnchor="middle"
                  fontSize={10} fontWeight={700} fill="#fff" fontFamily="monospace">CL</text>
              </g>
            );
          })()}
        </g>
        <polygon points={polyPts} fill="none" stroke="#c0392b" strokeWidth={2.5}/>

        {(()=>{
          const effCtrlXsvg = (ctrlX ?? maxW/2) * scale;
          const effCtrlYsvg = (ctrlY ?? maxH/2) * scale;
          const dashLen = 6;
          const dashGap = 4;

          // ── Effective inch values ──────────────────────────────────────────
          const effCtrlXin = ctrlX ?? maxW/2;
          const effCtrlYin = ctrlY ?? maxH/2;

          // ── Vertical line labels: ← left, → right ────────────────────────
          // Left wall is straight (x=0) so left = effCtrlXin always
          const vDistLeft = effCtrlXin;
          // Right wall is diagonal — sample at effCtrlYin (where horizontal line is)
          const vRightWallX = corners
            ? (wallXatY(corners.TR, corners.BR, effCtrlYin) ?? corners.TR[0])
            : maxW;
          const vDistRight = Math.max(0, vRightWallX - effCtrlXin);

          // ── Horizontal line labels: ↑ top, ↓ bottom ──────────────────────
          // Top wall is flat (y=0) so top = effCtrlYin always
          const hDistTop = effCtrlYin;
          // Bottom wall is diagonal — sample at effCtrlXin (where vertical line is)
          const hDistBot = corners
            ? Math.max(0, bottomWallY(effCtrlXin, corners.BL, corners.BR) - effCtrlYin)
            : Math.max(0, maxH - effCtrlYin);

          // Scale pill size based on SVG scale — larger pills on mobile (small scale)
          const pillFont = scale < 2 ? 13 : scale < 3 ? 11 : 8.5;
          const pillH    = scale < 2 ? 20 : scale < 3 ? 17 : 14;
          const pillGap  = scale < 2 ? 22 : scale < 3 ? 19 : 14; // vertical gap between pills
          const LabelPill = ({lx, ly, text, anchor, color}) => {
            const tw = text.length * (pillFont * 0.62) + 12;
            const th = pillH;
            const ox = anchor==='end' ? -tw : anchor==='middle' ? -tw/2 : 0;
            return (
              <g style={{pointerEvents:'none'}}>
                <rect x={lx+ox} y={ly-th+3} width={tw} height={th} rx={4}
                  fill={color} opacity={0.92}/>
                <text x={lx+ox+tw/2} y={ly-1} textAnchor="middle"
                  fontSize={pillFont} fontFamily="monospace" fontWeight="700" fill="#fff">
                  {text}
                </text>
              </g>
            );
          };

          return (
            <g style={{pointerEvents:'none'}}>
              {/* Vertical control line (blue) — solid+bright when near snap */}
              <line x1={effCtrlXsvg} y1={-PAD} x2={effCtrlXsvg} y2={H+PAD}
                stroke={snapNear.x ? "#5dade2" : "#2980b9"}
                strokeWidth={snapNear.x ? 2.5 : 1.5}
                strokeDasharray={snapNear.x ? "none" : `${dashLen} ${dashGap}`}
                opacity={0.9}/>

              {/* Vertical line: ← → at top only */}
              <LabelPill lx={effCtrlXsvg} ly={pillH} text={`← ${fmt(vDistLeft)}`}  anchor="end"   color="#2980b9"/>
              <LabelPill lx={effCtrlXsvg} ly={pillH} text={`→ ${fmt(vDistRight)}`} anchor="start" color="#1a5a8a"/>

              {/* Horizontal control line (green) — solid+bright when near snap */}
              <line x1={-PAD} y1={effCtrlYsvg} x2={W+PAD} y2={effCtrlYsvg}
                stroke={snapNear.y ? "#2ecc71" : "#27ae60"}
                strokeWidth={snapNear.y ? 2.5 : 1.5}
                strokeDasharray={snapNear.y ? "none" : `${dashLen} ${dashGap}`}
                opacity={0.9}/>

              {/* Horizontal line: ↑ ↓ at left only */}
              <LabelPill lx={6} ly={effCtrlYsvg}           text={`↑ ${fmt(hDistTop)}`} anchor="start" color="#27ae60"/>
              <LabelPill lx={6} ly={effCtrlYsvg+pillGap}   text={`↓ ${fmt(hDistBot)}`} anchor="start" color="#1a7040"/>

              {/* Intersection circle */}
              <circle cx={effCtrlXsvg} cy={effCtrlYsvg} r={5}
                fill="#fffdf8" stroke="#1a1410" strokeWidth={1.5} opacity={0.95}/>
              <circle cx={effCtrlXsvg} cy={effCtrlYsvg} r={2.5}
                fill="#1a1410" opacity={0.9}/>

              {/* Wider invisible hit areas for easier grabbing */}
              <line x1={effCtrlXsvg} y1={-PAD} x2={effCtrlXsvg} y2={H+PAD}
                stroke="transparent" strokeWidth={16}
                style={{cursor:'ew-resize',pointerEvents:'stroke'}}
                onMouseDown={e=>{e.stopPropagation();onPointerDown(e);}}
                onTouchStart={e=>{e.stopPropagation();onPointerDown(e);}}/>
              <line x1={-PAD} y1={effCtrlYsvg} x2={W+PAD} y2={effCtrlYsvg}
                stroke="transparent" strokeWidth={16}
                style={{cursor:'ns-resize',pointerEvents:'stroke'}}
                onMouseDown={e=>{e.stopPropagation();onPointerDown(e);}}
                onTouchStart={e=>{e.stopPropagation();onPointerDown(e);}}/>
            </g>
          );
        })()}
      </svg>

      <div style={{marginTop:14,display:'flex',flexDirection:'column',gap:8}}>
        {[['Horiz',sliderH,tileL,v=>applyShiftClamped(v,shiftRef.current.B)],
          ['Vert', sliderV,tileB,v=>applyShiftClamped(shiftRef.current.L,v)]
        ].map(([lbl,val,max,fn])=>(
          <div key={lbl} style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={S.sliderLabel}>{lbl}</span>
            <input type="range" min={0} max={max} step={1/32} value={val}
              onChange={e=>fn(parseFloat(e.target.value))}
              style={{flex:1,accentColor:'#c0392b',cursor:'pointer'}}/>
            <span style={S.sliderVal}>{fmt(val)}</span>
          </div>
        ))}
      </div>

      <div style={{display:'flex',gap:14,flexWrap:'wrap',alignItems:'center',
        marginTop:12,fontSize:'0.68rem',color:'#5a4e46'}}>
        {[['#f0e8dc','Full tile'],[CUT_COLOR,'Cut tile'],[GROUT_COLOR,'Grout']].map(([bg,lbl])=>(
          <span key={lbl} style={{display:'flex',alignItems:'center',gap:5}}>
            <span style={{width:11,height:11,background:bg,border:'1px solid #d4c8bc',
              display:'inline-block',borderRadius:1}}/>
            {lbl}
          </span>
        ))}
        <span style={{color:'#7a6858'}}>Drag to pan · Sliders · Arrow keys</span>
        {hasShift&&(
          <button onClick={()=>{ shiftRef.current={L:0,B:0}; onShiftChange?.(0,0); }}
            style={{marginLeft:'auto',fontFamily:'monospace',fontSize:'0.65rem',
              background:'none',border:'1px solid #d4c8bc',borderRadius:4,
              padding:'3px 10px',cursor:'pointer',color:'#5a4e46'}}>↺ Reset</button>
        )}
      </div>
    </div>
  );
}

function StatsRow({ layout, tileL, tileB, wastePct }) {
  if (!layout) return null;
  const cutTiles = layout.tiles?.filter(t=>t.isCut).length ?? 0;
  const eTileL = layout.tileL ?? tileL;
  const eTileB = layout.tileB ?? tileB;

  // Use max of slant edges — matches what is shown on the tile in the preview
  const smallestWidthCut = layout.tiles
    ?.filter(t => t.iW < eTileL - 1e-6)
    .reduce((min, t) => {
      const w = Math.max(t.iWtop ?? t.iW, t.iWbot ?? t.iW);
      if (!min) return { ...t, _displayW: w };
      return w < min._displayW ? { ...t, _displayW: w } : min;
    }, null);

  const smallestHeightCut = layout.tiles
    ?.filter(t => t.iH < eTileB - 1e-6)
    .reduce((min, t) => {
      const h = Math.max(t.iHleft ?? t.iH, t.iHright ?? t.iH);
      if (!min) return { ...t, _displayH: h };
      return h < min._displayH ? { ...t, _displayH: h } : min;
    }, null);

  const stats = [
    {label:'Total Tiles', value:layout.totalTiles??0, accent:true},
    {label:'Cut Tiles',   value:cutTiles},
    {label:'Total Cuts',  value:layout.totalCuts??0},
  ];

  const SmallCutCard = ({label, value, total}) => (
    <div style={{background:'#f5f0e8',border:'1.5px solid #d4c8bc',borderRadius:6,
      padding:'14px 18px',boxShadow:'3px 3px 0 #d4c8bc'}}>
      <div style={{fontSize:'0.6rem',letterSpacing:'0.15em',textTransform:'uppercase',
        color:'#5a4e46',marginBottom:4,fontFamily:'monospace'}}>{label}</div>
      {value != null ? (
        <div style={{fontFamily:'monospace',lineHeight:1.3}}>
          <div style={{fontSize:'1rem',fontWeight:700,color:'#c0392b'}}>{fmt(value)}</div>
          <div style={{fontSize:'0.62rem',color:'#9a8878',marginTop:2}}>{pct(value,total)}% of {fmt(total)}</div>
        </div>
      ) : (
        <div style={{fontSize:'1rem',fontWeight:600,color:'#1a1410',fontFamily:'monospace'}}>—</div>
      )}
    </div>
  );

  return (
    <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:14,marginBottom:20}}>
      {stats.map(s=>(
        <div key={s.label} style={{background:s.accent?'#c0392b':'#f5f0e8',
          border:`1.5px solid ${s.accent?'#a93226':'#d4c8bc'}`,borderRadius:6,
          padding:'14px 18px',boxShadow:s.accent?'3px 3px 0 #a93226':'3px 3px 0 #d4c8bc'}}>
          <div style={{fontSize:'0.6rem',letterSpacing:'0.15em',textTransform:'uppercase',
            color:s.accent?'rgba(255,255,255,0.75)':'#5a4e46',marginBottom:4,fontFamily:'monospace'}}>{s.label}</div>
          <div style={{fontSize:'1.6rem',fontWeight:600,color:s.accent?'#fff':'#1a1410',fontFamily:'monospace',lineHeight:1.1}}>{s.value}</div>
        </div>
      ))}
      <SmallCutCard label="Smallest Width Cut"  value={smallestWidthCut?._displayW}  total={eTileL}/>
      <SmallCutCard label="Smallest Height Cut" value={smallestHeightCut?._displayH} total={eTileB}/>
      <div style={{background:'#f5f0e8',border:'1.5px solid #d4c8bc',borderRadius:6,
        padding:'14px 18px',boxShadow:'3px 3px 0 #d4c8bc'}}>
        <div style={{fontSize:'0.6rem',letterSpacing:'0.15em',textTransform:'uppercase',
          color:'#5a4e46',marginBottom:4,fontFamily:'monospace'}}>Tile Waste</div>
        {wastePct != null ? (
          <div style={{fontFamily:'monospace',lineHeight:1.3}}>
            <div style={{fontSize:'1.6rem',fontWeight:700,lineHeight:1.1,
              color:parseFloat(wastePct)>30?'#c0392b':parseFloat(wastePct)>15?'#e67e22':'#27ae60'}}>
              {wastePct}%
            </div>
          </div>
        ) : (
          <div style={{fontSize:'1rem',fontWeight:600,color:'#1a1410',fontFamily:'monospace'}}>—</div>
        )}
      </div>
    </div>
  );
}


function CutPanel({ layout, tileL, tileB }) {
  const eTileL = layout?.tileL ?? tileL;
  const eTileB = layout?.tileB ?? tileB;
  if (!layout?.cutList?.length) return (
    <div style={{...S.card,textAlign:'center',color:'#5a4e46',fontSize:'0.82rem',padding:20}}>
      {layout ? 'No cuts needed.' : 'Enter dimensions to see cuts.'}
    </div>
  );
  return (
    <div style={S.card}>
      <div style={S.cardLabel}>Cut Analysis — updates live as you drag</div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {layout.cutList.map(cut=>{
          const wC=cut.w<eTileL-1e-6, hC=cut.h<eTileB-1e-6;
          const DW=60,DH=44;
          const pw=Math.max(2,Math.min(DW,cut.w/eTileL*DW));
          const ph=Math.max(2,Math.min(DH,cut.h/eTileB*DH));
          return (
            <div key={cut.id} style={{display:'flex',alignItems:'flex-start',gap:14,
              background:cut.cutsRequired===2?'#fff8f2':'#fffdf8',
              border:`1.5px solid ${cut.cutsRequired===2?'#e8a87a':'#e8c89a'}`,
              borderLeft:`4px solid ${CUT_COLOR}`,borderRadius:6,padding:'12px 14px'}}>
              <svg width={DW+6} height={DH+6} style={{flexShrink:0,marginTop:2}}>
                <rect x={3} y={3} width={DW} height={DH} fill="#e8ddd0" stroke="#c9b8a8" strokeWidth={1} rx={1}/>
                <rect x={3} y={3} width={pw} height={ph} fill={CUT_COLOR} opacity={0.85} rx={1}/>
                {wC&&<line x1={3+pw} y1={3} x2={3+pw} y2={3+DH} stroke="#c0392b" strokeWidth={1.5} strokeDasharray="3 2"/>}
                {hC&&<line x1={3} y1={3+ph} x2={3+DW} y2={3+ph} stroke="#c0392b" strokeWidth={1.5} strokeDasharray="3 2"/>}
              </svg>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:4}}>
                  <span style={{fontWeight:600,fontSize:'0.8rem',color:'#1a1410'}}>{cut.label}</span>
                  <span style={{background:CUT_COLOR,color:'#fff',fontSize:'0.6rem',borderRadius:3,padding:'1px 6px'}}>× {cut.count}</span>
                  <span style={{background:cut.cutsRequired===2?'#c0392b':'#9a8878',color:'#fff',fontSize:'0.6rem',borderRadius:3,padding:'1px 6px'}}>{cut.cutsRequired} cut{cut.cutsRequired>1?'s':''}</span>
                </div>
                <div style={{fontFamily:'monospace',fontSize:'0.85rem',color:'#1a1410',fontWeight:700,marginBottom:4}}>{fmt(wC?cut.w:eTileL)} × {fmt(hC?cut.h:eTileB)}</div>
                {wC&&<div style={{fontSize:'0.67rem',color:'#5a4e46'}}>Width: <strong>{fmt(cut.w)}</strong> from <strong>{fmt(eTileL)}</strong> — keep <strong>{pct(cut.w,eTileL)}%</strong></div>}
                {hC&&<div style={{fontSize:'0.67rem',color:'#5a4e46'}}>Height: <strong>{fmt(cut.h)}</strong> from <strong>{fmt(eTileB)}</strong> — keep <strong>{pct(cut.h,eTileB)}%</strong></div>}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{marginTop:12,fontSize:'0.65rem',color:'#9a8878',fontStyle:'italic'}}>* Installed dimension — grout not included.</div>
    </div>
  );
}

function RoomDiagram({ topW, leftH, rightH, bottomW }) {
  if (!topW||!leftH||!rightH||!bottomW) return null;
  const corners = solveCorners(topW,leftH,rightH,bottomW);
  if (!corners) return (
    <div style={{marginTop:8,fontSize:'0.7rem',color:'#c0392b',textAlign:'center'}}>
      These wall lengths do not form a valid room shape.
    </div>
  );
  const {TL,TR,BR,BL}=corners;
  const maxH=Math.max(BL[1],BR[1]);
  const sc=Math.min(130/topW,90/maxH);
  const pts=[TL,TR,BR,BL].map(p=>`${p[0]*sc},${p[1]*sc}`).join(' ');
  const bmx=(BL[0]+BR[0])/2*sc, bmy=(BL[1]+BR[1])/2*sc;
  return (
    <svg width={topW*sc+60} height={maxH*sc+36} style={{display:'block',margin:'12px auto',overflow:'visible'}}>
      <polygon points={pts} fill="#f5f0e8" stroke="#c0392b" strokeWidth={1.5}/>
      <text x={TR[0]*sc/2} y={-7} textAnchor="middle" fontSize={9} fill="#5a4e46" fontFamily="monospace">{fmt(topW)} top</text>
      <text x={-4} y={BL[1]*sc/2} textAnchor="end" dominantBaseline="middle" fontSize={9} fill="#5a4e46" fontFamily="monospace">{fmt(leftH)} left</text>
      <text x={TR[0]*sc+4} y={BR[1]*sc/2} textAnchor="start" dominantBaseline="middle" fontSize={9} fill="#5a4e46" fontFamily="monospace">{fmt(rightH)} right</text>
      <text x={bmx} y={bmy+14} textAnchor="middle" fontSize={9} fill="#5a4e46" fontFamily="monospace">{fmt(bottomW)} bottom</text>
    </svg>
  );
}

function FracInput({ label, value, onChange, placeholder }) {
  const [raw, setRaw] = useState(value??'');
  const [error, setError] = useState(false);
  useEffect(()=>{ if(value===''||value===undefined) setRaw(''); else if(value!==undefined) setRaw(String(value)); },[value]);
  function handleChange(e) {
    const v=e.target.value; setRaw(v);
    if(v===''){onChange('');setError(false);return;}
    const p=parseFraction(v);
    if(isFinite(p)&&p>0){onChange(String(p));setError(false);}
    else{onChange('');setError(v.length>0);}
  }
  return (
    <div style={{display:'flex',flexDirection:'column',gap:4}}>
      <label style={S.label}>{label}</label>
      <input type="text" value={raw} onChange={handleChange} placeholder={placeholder||'e.g. 216'}
        style={{...S.input,borderColor:error?'#c0392b':'#d4c8bc',background:error?'#fff5f5':'#f5f0e8'}}/>
      {error&&<span style={{fontSize:'0.6rem',color:'#c0392b',fontFamily:'monospace'}}>Use: 12 or 5 1/16 or 3/4</span>}
    </div>
  );
}

function CardLabel({ children }) {
  return <div style={S.cardLabel}>{children}<span style={{flex:1,height:1,background:'#d4c8bc'}}/></div>;
}

const S = {
  label:      {fontSize:'0.65rem',letterSpacing:'0.12em',textTransform:'uppercase',color:'#5a4e46',fontFamily:'monospace'},
  input:      {fontFamily:'monospace',fontSize:'1rem',color:'#1a1410',background:'#f5f0e8',border:'1.5px solid #d4c8bc',borderRadius:4,padding:'9px 12px',width:'100%',outline:'none'},
  select:     {fontFamily:'monospace',fontSize:'0.85rem',color:'#1a1410',background:'#f5f0e8',border:'1.5px solid #d4c8bc',borderRadius:4,padding:'9px 12px',width:'100%',outline:'none',cursor:'pointer'},
  card:       {background:'#fffdf8',border:'1.5px solid #d4c8bc',borderRadius:8,padding:'24px',boxShadow:'4px 4px 0 #d4c8bc'},
  cardLabel:  {fontSize:'0.6rem',letterSpacing:'0.18em',textTransform:'uppercase',color:'#5a4e46',marginBottom:18,display:'flex',alignItems:'center',gap:8,fontFamily:'monospace'},
  sliderLabel:{fontSize:'0.65rem',color:'#5a4e46',fontFamily:'monospace',width:80,flexShrink:0,textTransform:'uppercase',letterSpacing:'0.08em'},
  sliderVal:  {fontSize:'0.7rem',fontFamily:'monospace',color:'#9a8878',minWidth:52,textAlign:'right'},
};

export default function TileCalculator() {
  const [roomUnit,   setRoomUnit]   = useState('inches');
  const [tileUnit,   setTileUnit]   = useState('inches');
  const [topWall,    setTopWall]    = useState('');
  const [leftWall,   setLeftWall]   = useState('');
  const [rightWall,  setRightWall]  = useState('');
  const [bottomWall, setBottomWall] = useState('');
  const [tileL,      setTileL]      = useState('');
  const [tileB,      setTileB]      = useState('');
  const [gap,        setGap]        = useState('1/16');
  const [offset,     setOffset]     = useState(0);
  const [startMode,  setStartMode]  = useState('left');
  const [layout,     setLayout]     = useState(null);
  const [optimise,   setOptimise]   = useState(null);
  const [layoutDir,  setLayoutDir]  = useState('lr');
  const [isCustom,   setIsCustom]   = useState(false);
  const [shiftL,     setShiftL]     = useState(0);
  const [shiftB,     setShiftB]     = useState(0);
  const skipResetRef = useRef(false); // true while loading a saved layout
  const onLayoutChange = useCallback(l=>setLayout(l),[]);

  // ── API helpers ─────────────────────────────────────────────────────────────
  const BASE = `${process.env.REACT_APP_SERVER_URL}/tiles`;
  const [currentLayoutId, setCurrentLayoutId] = useState(null); // id of loaded layout
  const [saveStatus,      setSaveStatus]      = useState(null); // null | 'saving' | 'saved' | 'error'
  const [showLayouts,     setShowLayouts]      = useState(false);
  const [savedLayouts,    setSavedLayouts]     = useState([]);
  const [loadingLayouts,  setLoadingLayouts]   = useState(false);
  const [showSaveModal,   setShowSaveModal]    = useState(false);
  const [saveName,        setSaveName]         = useState('');

  const getPayload = useCallback((name) => ({
    name,
    top_wall:    parseFraction(topWall)    || 0,
    left_wall:   parseFraction(leftWall)   || 0,
    right_wall:  parseFraction(rightWall)  || 0,
    bottom_wall: parseFraction(bottomWall) || 0,
    room_unit:   roomUnit,
    tile_length: parseFraction(tileL)      || 0,
    tile_width:  parseFraction(tileB)      || 0,
    tile_unit:   tileUnit,
    gap,
    offset,
    start_mode:  startMode,
    layout_dir:  layoutDir,
    shift_l:     shiftL,
    shift_b:     shiftB,
  }), [topWall,leftWall,rightWall,bottomWall,roomUnit,tileL,tileB,tileUnit,
       gap,offset,startMode,layoutDir,shiftL,shiftB]);

  const loadLayoutData = useCallback((d) => {
    skipResetRef.current = true; // prevent reset effect from wiping loaded shifts
    setRoomUnit(d.room_unit   || 'inches');
    setTileUnit(d.tile_unit   || 'inches');
    setTopWall(   String(d.top_wall));
    setLeftWall(  String(d.left_wall));
    setRightWall( String(d.right_wall));
    setBottomWall(String(d.bottom_wall));
    setTileL(     String(d.tile_length));
    setTileB(     String(d.tile_width));
    setGap(       d.gap        || '1/16');
    // Snap offset to nearest known value to handle float precision from DB
    const rawOffset = Number(d.offset) || 0;
    const knownOffsets = [0, 0.25, 1/3, 0.5];
    const snappedOffset = knownOffsets.reduce((a,b) => Math.abs(b-rawOffset) < Math.abs(a-rawOffset) ? b : a);
    setOffset(snappedOffset);
    setStartMode( d.start_mode || 'left');
    setLayoutDir( d.layout_dir || 'lr');
    setShiftL(    Number(d.shift_l) || 0);
    setShiftB(    Number(d.shift_b) || 0);
    setTimeout(() => { skipResetRef.current = false; }, 200);
  }, []);

  const fetchLayouts = useCallback(async () => {
    setLoadingLayouts(true);
    try {
      const response = await axios.get(`${BASE}`);
      setSavedLayouts(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      console.error('Failed to fetch layouts', e);
    } finally {
      setLoadingLayouts(false);
    }
  }, [BASE]);

  const handleSave = useCallback(async () => {
    if (!saveName.trim()) return;
    setSaveStatus('saving');
    try {
      const payload = getPayload(saveName.trim());
      if (currentLayoutId) {
        await axios.patch(`${BASE}/${currentLayoutId}`, payload);
      } else {
        const response = await axios.post(`${BASE}`, payload);
        if (response.data?.data) setCurrentLayoutId(response.data.data);
      }
      setSaveStatus('saved');
      setShowSaveModal(false);
      setTimeout(() => setSaveStatus(null), 2500);
    } catch (e) {
      console.error('Save failed', e);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2500);
    }
  }, [saveName, currentLayoutId, getPayload, BASE]);

  const handleDelete = useCallback(async (id) => {
    try {
      await axios.delete(`${BASE}/${id}`);
      setSavedLayouts(prev => prev.filter(l => l.id !== id));
      if (currentLayoutId === id) setCurrentLayoutId(null);
    } catch (e) {
      console.error('Delete failed', e);
    }
  }, [currentLayoutId, BASE]);

  const handleLoad = useCallback(async (layout) => {
    loadLayoutData(layout);
    setCurrentLayoutId(layout.id);
    setSaveName(layout.name);
    setShowLayouts(false);
  }, [loadLayoutData]);

  const openSaveModal = () => {
    setSaveName(prev => prev || 'Untitled Layout');
    setShowSaveModal(true);
  };

  const calc = useMemo(()=>{
    const tw=parseFraction(topWall),lh=parseFraction(leftWall);
    const rh=parseFraction(rightWall),bw=parseFraction(bottomWall);
    const tL=parseFraction(tileL),tBv=parseFraction(tileB);
    if(!isOk(tw)||!isOk(lh)||!isOk(rh)||!isOk(bw)||!isOk(tL)||!isOk(tBv)) return null;
    const topW=toInches(tw,roomUnit),leftH=toInches(lh,roomUnit);
    const rightH=toInches(rh,roomUnit),bottomW2=toInches(bw,roomUnit);
    const tileLIn=toInches(tL,tileUnit),tileBIn=toInches(tBv,tileUnit);
    const gapIn=gap==='1/8'?0.125:0.0625;
    if(!isOk(topW)||!isOk(leftH)||!isOk(rightH)||!isOk(bottomW2)||!isOk(tileLIn)||!isOk(tileBIn)) return null;
    const corners=solveCorners(topW,leftH,rightH,bottomW2);
    if(!corners) return null;
    const {TL,TR,BR,BL}=corners; const pts=[TL,TR,BR,BL]; let area=0;
    for(let i=0;i<4;i++){const[x1,y1]=pts[i],[x2,y2]=pts[(i+1)%4];area+=x1*y2-x2*y1;}
    return {topW,leftH,rightH,bottomW:bottomW2,tileLIn,tileBIn,gapIn,
      scale:computeScale(topW,leftH,rightH,bottomW2),
      areaSqFt:(Math.abs(area)/2/144).toFixed(2),
      tileAreaSqFt:((tileLIn/12)*(tileBIn/12)).toFixed(3)};
  },[topWall,leftWall,rightWall,bottomWall,tileL,tileB,gap,roomUnit,tileUnit]);

  const wastePct = useMemo(() => {
    if (!layout?.tiles?.length || !calc) return null;
    const tileArea = calc.tileLIn * calc.tileBIn;
    const totalArea = layout.tiles.length * tileArea;
    const wastedArea = layout.tiles
      .filter(t => t.isCut)
      .reduce((sum, t) => sum + (tileArea - t.iW * t.iH), 0);
    return totalArea > 0 ? (wastedArea / totalArea * 100).toFixed(1) : null;
  }, [layout, calc]);

  const reuseResult = useMemo(() => {
    if (!layout?.tiles?.length || !calc) return null;
    const tileL = calc.tileLIn, tileB = calc.tileBIn;
    const EPS = 1e-6;

    const cutTiles = layout.tiles.filter(t => t.isCut);
    const nc = cutTiles.length;
    const saved_set = new Set();
    const must_purchase = new Set();
    const offcutUsed = new Set();

    const order = [...Array(nc).keys()].sort((a, b) =>
      cutTiles[b].iW * cutTiles[b].iH - cutTiles[a].iW * cutTiles[a].iH);

    let saved = 0, recoveredArea = 0;
    const matches = [];

    for (const i of order) {
      if (saved_set.has(i) || must_purchase.has(i)) continue;
      const dem = cutTiles[i];
      let bestJ = -1, bestOcType = null, bestArea = Infinity;

      for (let j = 0; j < nc; j++) {
        if (j === i || saved_set.has(j)) continue;
        const t = cutTiles[j];
        if (t.iW < tileL - EPS && !offcutUsed.has(j + 'w')) {
          const ow = tileL - t.iW, oh = tileB;
          if (ow >= dem.iW - EPS && oh >= dem.iH - EPS) {
            const a = ow * oh;
            if (a < bestArea) { bestArea = a; bestJ = j; bestOcType = 'w'; }
          }
        }
        if (t.iH < tileB - EPS && !offcutUsed.has(j + 'h')) {
          const ow = tileL, oh = tileB - t.iH;
          if (ow >= dem.iW - EPS && oh >= dem.iH - EPS) {
            const a = ow * oh;
            if (a < bestArea) { bestArea = a; bestJ = j; bestOcType = 'h'; }
          }
        }
      }

      if (bestJ !== -1) {
        saved_set.add(i);
        must_purchase.add(bestJ);
        offcutUsed.add(bestJ + bestOcType);
        recoveredArea += dem.iW * dem.iH;
        matches.push({ savedIdx: i, sourceIdx: bestJ });
        saved++;
      }
    }

    if (saved === 0) return null;

    const tileArea = tileL * tileB;
    const adjustedTotal = layout.totalTiles - saved;
    const wastedArea = layout.tiles
      .filter(t => t.isCut)
      .reduce((sum, t) => sum + (tileArea - t.iW * t.iH), 0);
    const revisedWaste = adjustedTotal > 0
      ? (Math.max(0, wastedArea - recoveredArea) / (adjustedTotal * tileArea) * 100)
      : 0;

    const STAR_COLORS = ['#27ae60','#2980b9','#8e44ad','#e67e22','#16a085','#d35400'];
    const sortedMatches = [...matches].sort((a, b) => {
      const ai = layout.tiles.indexOf(cutTiles[a.sourceIdx]);
      const bi = layout.tiles.indexOf(cutTiles[b.sourceIdx]);
      return ai - bi;
    });
    const reusePairs = new Map();
    sortedMatches.forEach(({ savedIdx, sourceIdx }, gIdx) => {
      const color = STAR_COLORS[gIdx % STAR_COLORS.length];
      const groupNum = gIdx + 1;
      const savedTile  = cutTiles[savedIdx];
      const sourceTile = cutTiles[sourceIdx];
      const savedLayoutIdx  = layout.tiles.indexOf(savedTile);
      const sourceLayoutIdx = layout.tiles.indexOf(sourceTile);
      if (savedLayoutIdx  !== -1) reusePairs.set(savedLayoutIdx,  { groupNum, color, role: 'reused' });
      if (sourceLayoutIdx !== -1) reusePairs.set(sourceLayoutIdx, { groupNum, color, role: 'source' });
    });

    return {
      saved,
      originalTotal: layout.totalTiles,
      adjustedTotal,
      revisedWaste: Math.max(0, revisedWaste).toFixed(1),
      reusePairs,
    };
  }, [layout, calc]);

  const STARTS = [
    { value: 'left',   label: 'Left wall' },
    { value: 'right',  label: 'Right wall' },
    { value: 'top',    label: 'Top wall' },
    { value: 'bottom', label: 'Bottom wall' },
    { value: 'center', label: 'Center line' },
  ];

  const bestResult = useMemo(() => {
    if (!calc || !optimise) return null;
    const { topW, leftH, rightH, bottomW, tileLIn, tileBIn, gapIn } = calc;
    const pitchL = tileLIn + gapIn, pitchB = tileBIn + gapIn;
    const wrap = (v, p) => ((v % p) + p) % p;
    const corners0 = solveCorners(topW, leftH, rightH, bottomW);
    if (!corners0) return null;
    const maxH0 = Math.max(corners0.BL[1], corners0.BR[1]);
    let best = null;
    for (const st of STARTS) {
      let bL = 0, bB = 0;
      if      (st.value === 'right')  bL = wrap(topW, pitchL);
      else if (st.value === 'bottom') bB = wrap(maxH0, pitchB);
      else if (st.value === 'center') bL = ((topW / 2 + gapIn / 2) % pitchL + pitchL) % pitchL;
      const result = calcLayout(topW, leftH, rightH, bottomW, tileLIn, tileBIn, gapIn,
        wrap(bL, pitchL), wrap(bB, pitchB), offset, layoutDir);
      const primary   = optimise === 'cuts' ? result.totalCuts  : result.totalTiles;
      const secondary = optimise === 'cuts' ? result.totalTiles : result.totalCuts;
      if (!best ||
          primary < best.primary ||
          (primary === best.primary && secondary < best.secondary)) {
        best = { primary, secondary, startMode: st.value, stLabel: st.label,
                 totalTiles: result.totalTiles, totalCuts: result.totalCuts };
      }
    }
    return best;
  }, [calc, optimise, offset, layoutDir]);

  useEffect(() => {
    if (!bestResult) return;
    setStartMode(bestResult.startMode);
    setIsCustom(false);
  }, [bestResult]);

  const roomCard = (
    <div style={S.card}>
      <CardLabel>Room — all 4 walls</CardLabel>
      <div style={{marginBottom:12}}>
        <label style={S.label}>Unit</label>
        <select value={roomUnit} onChange={e=>setRoomUnit(e.target.value)} style={{...S.select,marginTop:5}}>
          {UNIT_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
        <FracInput label="Top"    value={topWall}    onChange={setTopWall}    placeholder="e.g. 216"/>
        <FracInput label="Bottom" value={bottomWall} onChange={setBottomWall} placeholder="e.g. 216"/>
        <FracInput label="Left"   value={leftWall}   onChange={setLeftWall}   placeholder="e.g. 132"/>
        <FracInput label="Right"  value={rightWall}  onChange={setRightWall}  placeholder="e.g. 132"/>
      </div>
      {calc&&<div style={{marginTop:10,fontSize:'0.72rem',color:'#5a4e46',background:'#f5f0e8',padding:'7px 10px',borderRadius:4,border:'1px solid #d4c8bc'}}>Area ≈ <strong>{calc.areaSqFt} sq ft</strong></div>}
      {calc&&<RoomDiagram topW={calc.topW} leftH={calc.leftH} rightH={calc.rightH} bottomW={calc.bottomW}/>}
      {!calc&&topWall&&leftWall&&rightWall&&bottomWall&&(
        <RoomDiagram
          topW={toInches(parseFraction(topWall),roomUnit)}
          leftH={toInches(parseFraction(leftWall),roomUnit)}
          rightH={toInches(parseFraction(rightWall),roomUnit)}
          bottomW={toInches(parseFraction(bottomWall),roomUnit)}/>
      )}
    </div>
  );

  const tileCard = (
    <div style={S.card}>
      <CardLabel>Tile</CardLabel>
      <div style={{marginBottom:12}}>
        <label style={S.label}>Unit</label>
        <select value={tileUnit} onChange={e=>setTileUnit(e.target.value)} style={{...S.select,marginTop:5}}>
          {UNIT_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        <FracInput label="Length" value={tileL} onChange={setTileL} placeholder="e.g. 15"/>
        <FracInput label="Width"  value={tileB} onChange={setTileB} placeholder="e.g. 15"/>
      </div>
      {calc&&<div style={{marginTop:10,fontSize:'0.72rem',color:'#5a4e46',background:'#f5f0e8',padding:'7px 10px',borderRadius:4,border:'1px solid #d4c8bc'}}>Tile area: <strong>{calc.tileAreaSqFt} sq ft</strong></div>}
    </div>
  );

  const controlsContent = (
    <>
      <div style={S.card}>
        <CardLabel>Gap</CardLabel>
        <select value={gap} onChange={e=>setGap(e.target.value)} style={S.select}>
          <option value="1/16">1/16″ (1.6 mm) — Fine</option>
          <option value="1/8">1/8″ (3.2 mm) — Standard</option>
        </select>
        <div style={{marginTop:8,fontSize:'0.68rem',color:'#5a4e46'}}>{gap==='1/16'?'Rectified tiles':'Standard ceramic / porcelain'}</div>
      </div>
      <div style={S.card}>
        <CardLabel>Row Pattern</CardLabel>
        <select value={offset} onChange={e=>setOffset(parseFloat(e.target.value))} style={S.select}>
          <option value={0}>Straight lay (0 offset)</option>
          <option value={0.5}>½ offset — Running bond</option>
          <option value={1/3}>⅓ offset — Roman bond</option>
          <option value={0.25}>¼ offset</option>
        </select>
      </div>
      <div style={S.card}>
        <CardLabel>Start Position</CardLabel>
        <select value={startMode} onChange={e=>{setStartMode(e.target.value);if(optimise)setIsCustom(true);}} style={S.select}>
          <option value="left">Left wall</option>
          <option value="right">Right wall</option>
          <option value="top">Top wall</option>
          <option value="bottom">Bottom wall</option>
          <option value="center">Center line (symmetric)</option>
        </select>
        <div style={{marginTop:8,fontSize:'0.68rem',color:'#5a4e46'}}>
          {startMode==='left'?'Full tiles from left → cut on right'
          :startMode==='right'?'Full tiles from right → cut on left'
          :startMode==='top'?'Full tiles from top → cut on bottom'
          :startMode==='bottom'?'Full tiles from bottom → cut on top'
          :'Grout line at center — equal cuts left & right'}
        </div>
      </div>
      <div style={S.card}>
        <CardLabel>Lay Direction</CardLabel>
        <div style={{display:'flex',gap:8}}>
          {[['lr','Left → Right'],['tb','Top → Bottom']].map(([val,lbl])=>(
            <label key={val} style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',
              gap:6,cursor:'pointer',padding:'9px 6px',borderRadius:4,fontFamily:'monospace',
              fontSize:'0.75rem',border:`1.5px solid ${layoutDir===val?'#c0392b':'#d4c8bc'}`,
              background:layoutDir===val?'#c0392b':'#f5f0e8',
              color:layoutDir===val?'#fff':'#1a1410',userSelect:'none'}}>
              <input type="radio" name="layoutDir" value={val}
                checked={layoutDir===val} onChange={()=>setLayoutDir(val)}
                style={{display:'none'}}/>
              {lbl}
            </label>
          ))}
        </div>
        <div style={{marginTop:8,fontSize:'0.68rem',color:'#5a4e46'}}>
          {layoutDir==='lr'?'Rows fill left to right':'Columns fill top to bottom'}
        </div>
      </div>
    </>
  );

  const bestLayoutCard = calc && (
    <div style={S.card}>
      <CardLabel>Best Layout</CardLabel>
      <div style={{display:'flex',gap:24,marginBottom:16}}>
        {[['cuts','Min Cuts'],['tiles','Min Tiles']].map(([val,lbl])=>(
          <label key={val} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',
            fontSize:'0.82rem',fontFamily:'monospace',color:'#1a1410',userSelect:'none'}}>
            <input type="radio" name="optimise" value={val}
              checked={optimise===val}
              onChange={()=>{setOptimise(val);setIsCustom(false);}}
              style={{accentColor:'#c0392b',width:15,height:15,cursor:'pointer'}}/>
            {lbl}
          </label>
        ))}
        {optimise && (
          <button onClick={()=>setOptimise(null)}
            style={{marginLeft:'auto',fontFamily:'monospace',fontSize:'0.65rem',
              background:'none',border:'1px solid #d4c8bc',borderRadius:4,
              padding:'3px 10px',cursor:'pointer',color:'#5a4e46'}}>
            Clear
          </button>
        )}
      </div>
      {optimise && bestResult && !isCustom && (
        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',
          background:'#1a1410',borderRadius:6,padding:'12px 16px'}}>
          <span style={{fontSize:'0.7rem',color:'#9a8878',fontFamily:'monospace',
            letterSpacing:'0.1em',textTransform:'uppercase'}}>Best</span>
          <span style={{fontFamily:'monospace',fontSize:'0.85rem',fontWeight:700,
            color:'#f5f0e8'}}>{bestResult.stLabel}</span>
          <span style={{marginLeft:'auto',display:'flex',gap:12}}>
            <span style={{fontFamily:'monospace',fontSize:'0.78rem',
              color: optimise==='tiles' ? '#f4a56a' : '#9a8878'}}>
              {bestResult.totalTiles} tiles
            </span>
            <span style={{fontFamily:'monospace',fontSize:'0.78rem',
              color: optimise==='cuts' ? '#f4a56a' : '#9a8878'}}>
              {bestResult.totalCuts} cuts
            </span>
          </span>
        </div>
      )}
      {optimise && isCustom && (
        <div style={{display:'flex',alignItems:'center',gap:10,
          background:'#f5f0e8',borderRadius:6,padding:'12px 16px',
          border:'1.5px dashed #d4c8bc'}}>
          <span style={{fontFamily:'monospace',fontSize:'0.78rem',color:'#9a8878'}}>
            Custom layout — deviating from best
          </span>
          <button onClick={()=>{if(bestResult){setOffset(bestResult.offset);setStartMode(bestResult.startMode);setIsCustom(false);}}}
            style={{marginLeft:'auto',fontFamily:'monospace',fontSize:'0.65rem',
              background:'#c0392b',border:'none',borderRadius:4,
              padding:'4px 12px',cursor:'pointer',color:'#fff'}}>
            Restore best
          </button>
        </div>
      )}
      {!optimise && (
        <div style={{fontSize:'0.75rem',color:'#9a8878',fontFamily:'monospace'}}>
          Select a metric above to auto-find the best start position using the current row pattern.
        </div>
      )}
    </div>
  );

  const analysisContent = (
    <>
      {wastePct !== null && (
        <div style={{...S.card,marginBottom:20}}>
          <div style={{display:'flex',alignItems:'center',gap:24,flexWrap:'wrap',
            marginBottom: reuseResult ? 16 : 0}}>
            <div>
              <div style={{fontSize:'0.6rem',letterSpacing:'0.15em',textTransform:'uppercase',
                color:'#5a4e46',fontFamily:'monospace',marginBottom:4}}>Tile Waste</div>
              <div style={{fontSize:'1.8rem',fontWeight:700,fontFamily:'monospace',
                color: parseFloat(wastePct)>30?'#c0392b':parseFloat(wastePct)>15?'#e67e22':'#27ae60'}}>
                {wastePct}%
              </div>
            </div>
            <div style={{fontSize:'0.72rem',color:'#5a4e46',lineHeight:1.6}}>
              Cut-off area wasted from<br/>total tiles purchased
            </div>
          </div>
          {reuseResult && (
            <>
              <div style={{height:1,background:'#d4c8bc',marginBottom:16}}/>
              <div style={{display:'flex',alignItems:'flex-start',gap:20,flexWrap:'wrap'}}>
                <div style={{background:'#27ae60',borderRadius:6,padding:'12px 18px',
                  minWidth:100,textAlign:'center',boxShadow:'3px 3px 0 #1e8449'}}>
                  <div style={{fontSize:'2rem',fontWeight:700,color:'#fff',fontFamily:'monospace',lineHeight:1}}>
                    {reuseResult.saved}
                  </div>
                  <div style={{fontSize:'0.6rem',color:'rgba(255,255,255,0.85)',
                    textTransform:'uppercase',letterSpacing:'0.12em',marginTop:4}}>
                    tiles saved
                  </div>
                </div>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{fontSize:'0.6rem',letterSpacing:'0.15em',textTransform:'uppercase',
                    color:'#5a4e46',fontFamily:'monospace',marginBottom:8}}>Offcut Reuse</div>
                  <div style={{fontFamily:'monospace',fontSize:'0.82rem',color:'#1a1410',lineHeight:2}}>
                    <div>
                      Original: <strong>{reuseResult.originalTotal} tiles</strong>
                      <span style={{margin:'0 8px',color:'#9a8878'}}>→</span>
                      <strong style={{color:'#27ae60'}}>Adjusted: {reuseResult.adjustedTotal} tiles</strong>
                    </div>
                    <div style={{fontSize:'0.72rem',color:'#5a4e46'}}>
                      Revised waste after reuse:&nbsp;
                      <strong style={{
                        color: parseFloat(reuseResult.revisedWaste)>30?'#c0392b'
                             : parseFloat(reuseResult.revisedWaste)>15?'#e67e22':'#27ae60'}}>
                        {reuseResult.revisedWaste}%
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      <div style={{marginBottom:20}}>
        <CutPanel layout={layout} tileL={calc?.tileLIn} tileB={calc?.tileBIn}/>
      </div>
      <div style={{...S.card,background:'#1a1410',border:'1.5px solid #333',boxShadow:'4px 4px 0 #333'}}>
        <div style={{fontSize:'0.6rem',letterSpacing:'0.18em',textTransform:'uppercase',color:'#9a8878',marginBottom:14}}>Summary</div>
        <div style={{color:'#c9b8a8',fontSize:'0.85rem',lineHeight:2,fontFamily:'monospace'}}>
          {calc && <>
            <div>Room: <strong style={{color:'#f5f0e8'}}>Top {fmt(calc.topW)} · Bottom {fmt(calc.bottomW)} · Left {fmt(calc.leftH)} · Right {fmt(calc.rightH)}</strong></div>
            <div>Area: <strong style={{color:'#f5f0e8'}}>≈ {calc.areaSqFt} sq ft</strong></div>
            <div>Tile: <strong style={{color:'#f5f0e8'}}>{fmt(calc.tileLIn)} × {fmt(calc.tileBIn)}</strong> · Gap: <strong style={{color:'#f5f0e8'}}>{gap}″</strong></div>
          </>}
          {layout&&<div>Layout: <strong style={{color:'#f5f0e8'}}>{layout.totalTiles} tiles · {layout.totalCuts} cuts</strong></div>}
        </div>
      </div>
    </>
  );

  const tilePreviewEl = (
    <TilePreview
      topW={calc?.topW} leftH={calc?.leftH} rightH={calc?.rightH} bottomW={calc?.bottomW}
      tileL={calc?.tileLIn} tileB={calc?.tileBIn} gapIn={calc?.gapIn}
      offset={offset} startMode={startMode} scale={calc?.scale}
      onLayoutChange={onLayoutChange}
      reusePairs={reuseResult?.reusePairs ?? null}
      layoutDir={layoutDir}
      externalShiftL={shiftL} externalShiftB={shiftB}
      onShiftChange={(l,b)=>{ setShiftL(l); setShiftB(b); }}
      skipResetRef={skipResetRef}
    />
  );

  // ── Mobile detection ────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // ── Bottom sheet state ──────────────────────────────────────────────────────
  const [sheet, setSheet] = useState(null); // null | 'settings' | 'analysis'
  const toggleSheet = (name) => setSheet(s => s === name ? null : name);

  // ── Side drawer (Settings) ─────────────────────────────────────────────────
  const SideDrawer = ({ open, onClose, title, children }) => {
    useEffect(() => {
      if (open) document.body.style.overflow = 'hidden';
      else document.body.style.overflow = '';
      return () => { document.body.style.overflow = ''; };
    }, [open]);
    return (
      <>
        {open && (
          <div onClick={onClose}
            style={{position:'fixed',inset:0,background:'rgba(26,20,16,0.45)',
              zIndex:40,touchAction:'none'}}/>
        )}
        <div style={{position:'fixed',top:0,left:0,bottom:0,width:'82vw',maxWidth:320,
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition:'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
          zIndex:50,background:'#f5f0e8',boxShadow:'4px 0 24px rgba(26,20,16,0.18)',
          display:'flex',flexDirection:'column'}}>
          {/* Header */}
          <div style={{padding:'20px 16px 12px',flexShrink:0,
            borderBottom:'1.5px solid #d4c8bc'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontFamily:'monospace',fontWeight:700,fontSize:'0.85rem',
                letterSpacing:'0.1em',textTransform:'uppercase',color:'#1a1410'}}>{title}</span>
              <button onClick={onClose}
                style={{background:'none',border:'1px solid #d4c8bc',borderRadius:4,
                  padding:'4px 10px',fontFamily:'monospace',fontSize:'0.7rem',
                  cursor:'pointer',color:'#5a4e46'}}>✕</button>
            </div>
          </div>
          {/* Scrollable content */}
          <div style={{overflowY:'auto',padding:'16px 16px 40px',flex:1}}>
            {children}
          </div>
        </div>
      </>
    );
  };

  // ── Bottom sheet (Analysis) ─────────────────────────────────────────────────
  const BottomSheet = ({ open, onClose, title, children }) => {
    useEffect(() => {
      if (open) document.body.style.overflow = 'hidden';
      else document.body.style.overflow = '';
      return () => { document.body.style.overflow = ''; };
    }, [open]);
    return (
      <>
        {open && (
          <div onClick={onClose}
            style={{position:'fixed',inset:0,background:'rgba(26,20,16,0.45)',
              zIndex:40,touchAction:'none'}}/>
        )}
        <div style={{position:'fixed',left:0,right:0,bottom:0,
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition:'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
          zIndex:50,background:'#f5f0e8',borderRadius:'16px 16px 0 0',
          boxShadow:'0 -4px 24px rgba(26,20,16,0.18)',
          maxHeight:'82vh',display:'flex',flexDirection:'column'}}>
          <div style={{padding:'12px 20px 0',flexShrink:0}}>
            <div style={{width:36,height:4,borderRadius:2,background:'#d4c8bc',margin:'0 auto 14px'}}/>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <span style={{fontFamily:'monospace',fontWeight:700,fontSize:'0.85rem',
                letterSpacing:'0.1em',textTransform:'uppercase',color:'#1a1410'}}>{title}</span>
              <button onClick={onClose}
                style={{background:'none',border:'1px solid #d4c8bc',borderRadius:4,
                  padding:'4px 10px',fontFamily:'monospace',fontSize:'0.7rem',
                  cursor:'pointer',color:'#5a4e46'}}>✕ Close</button>
            </div>
            <div style={{height:1,background:'#d4c8bc',marginBottom:4}}/>
          </div>
          <div style={{overflowY:'auto',padding:'16px 20px 40px',flex:1}}>
            {children}
          </div>
        </div>
      </>
    );
  };

  // ── Mobile stats row: 2-col grid ────────────────────────────────────────────
  const MobileStatsRow = () => {
    if (!layout) return null;
    const cutTiles = layout.tiles?.filter(t=>t.isCut).length ?? 0;
    const eTileL = layout.tileL ?? calc?.tileLIn;
    const eTileB = layout.tileB ?? calc?.tileBIn;
    const smallestWidthCut = layout.tiles
      ?.filter(t => t.iW < eTileL - 1e-6)
      .reduce((min, t) => {
        const w = Math.max(t.iWtop ?? t.iW, t.iWbot ?? t.iW);
        return !min || w < min._dW ? { ...t, _dW: w } : min;
      }, null);
    const smallestHeightCut = layout.tiles
      ?.filter(t => t.iH < eTileB - 1e-6)
      .reduce((min, t) => {
        const h = Math.max(t.iHleft ?? t.iH, t.iHright ?? t.iH);
        return !min || h < min._dH ? { ...t, _dH: h } : min;
      }, null);

    const cards = [
      { label: 'Total Tiles',         value: layout.totalTiles ?? 0,       accent: true },
      { label: 'Cut Tiles',           value: cutTiles },
      { label: 'Total Cuts',          value: layout.totalCuts ?? 0 },
      { label: 'Tile Waste',          value: wastePct != null ? `${wastePct}%` : '—',
        color: wastePct != null ? (parseFloat(wastePct)>30?'#c0392b':parseFloat(wastePct)>15?'#e67e22':'#27ae60') : '#1a1410' },
      { label: 'Smallest Width Cut',  value: smallestWidthCut  ? fmt(smallestWidthCut._dW)  : '—',
        sub: smallestWidthCut  ? `${pct(smallestWidthCut._dW,  eTileL)}% of ${fmt(eTileL)}` : null },
      { label: 'Smallest Height Cut', value: smallestHeightCut ? fmt(smallestHeightCut._dH) : '—',
        sub: smallestHeightCut ? `${pct(smallestHeightCut._dH, eTileB)}% of ${fmt(eTileB)}` : null },
    ];

    return (
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
        {cards.map(c => (
          <div key={c.label} style={{background: c.accent ? '#c0392b' : '#fffdf8',
            border:`1.5px solid ${c.accent?'#a93226':'#d4c8bc'}`,borderRadius:6,
            padding:'12px 14px',boxShadow:c.accent?'2px 2px 0 #a93226':'2px 2px 0 #d4c8bc'}}>
            <div style={{fontSize:'0.55rem',letterSpacing:'0.12em',textTransform:'uppercase',
              color:c.accent?'rgba(255,255,255,0.75)':'#5a4e46',marginBottom:3,fontFamily:'monospace'}}>{c.label}</div>
            <div style={{fontSize:'1.3rem',fontWeight:700,fontFamily:'monospace',lineHeight:1.1,
              color:c.color||(c.accent?'#fff':'#1a1410')}}>{c.value}</div>
            {c.sub && <div style={{fontSize:'0.55rem',color:'#9a8878',fontFamily:'monospace',marginTop:2}}>{c.sub}</div>}
          </div>
        ))}
      </div>
    );
  };

  // ── Shared UI: Save Modal, Layouts Panel, Status ──────────────────────────
  const SaveModal = () => (
    <>
      <div onClick={()=>setShowSaveModal(false)}
        style={{position:'fixed',inset:0,background:'rgba(26,20,16,0.5)',zIndex:60}}/>
      <div style={{position:'fixed',top:'50%',left:'50%',
        transform:'translate(-50%,-50%)',zIndex:70,
        background:'#f5f0e8',borderRadius:10,padding:'28px 24px',
        width:'min(90vw,380px)',boxShadow:'6px 6px 0 #d4c8bc',
        border:'1.5px solid #d4c8bc'}}>
        <div style={{fontFamily:'monospace',fontWeight:700,fontSize:'0.85rem',
          letterSpacing:'0.12em',textTransform:'uppercase',color:'#1a1410',marginBottom:16}}>
          {currentLayoutId ? 'Update Layout' : 'Save Layout'}
        </div>
        <label style={S.label}>Layout Name</label>
        <input
          autoFocus
          type="text"
          value={saveName}
          onChange={e=>setSaveName(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&handleSave()}
          placeholder="e.g. Kitchen Floor"
          style={{...S.input,marginTop:6,marginBottom:16}}/>
        <div style={{display:'flex',gap:10}}>
          <button onClick={handleSave} disabled={!saveName.trim()}
            style={{flex:1,padding:'10px',fontFamily:'monospace',fontSize:'0.82rem',
              fontWeight:700,background:saveName.trim()?'#c0392b':'#d4c8bc',
              color:'#fff',border:'none',borderRadius:5,cursor:saveName.trim()?'pointer':'default',
              boxShadow:saveName.trim()?'3px 3px 0 #a93226':'none'}}>
            {saveStatus==='saving' ? 'Saving…' : currentLayoutId ? 'Update' : 'Save'}
          </button>
          <button onClick={()=>setShowSaveModal(false)}
            style={{padding:'10px 16px',fontFamily:'monospace',fontSize:'0.82rem',
              background:'none',border:'1.5px solid #d4c8bc',borderRadius:5,cursor:'pointer',color:'#5a4e46'}}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );

  const LayoutsPanel = ({ asSheet=false }) => (
    <div style={{display:'flex',flexDirection:'column',gap:0}}>
      {loadingLayouts ? (
        <div style={{textAlign:'center',padding:'32px',color:'#9a8878',fontFamily:'monospace',fontSize:'0.8rem'}}>
          Loading…
        </div>
      ) : savedLayouts.length === 0 ? (
        <div style={{textAlign:'center',padding:'32px',color:'#9a8878',fontFamily:'monospace',fontSize:'0.8rem'}}>
          No saved layouts yet.
        </div>
      ) : savedLayouts.map(l => (
        <div key={l.id}
          style={{display:'flex',alignItems:'center',gap:10,padding:'12px 0',
            borderBottom:'1px solid #e8e0d8',cursor:'pointer'}}
          onClick={()=>handleLoad(l)}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:'monospace',fontWeight:700,fontSize:'0.82rem',
              color: currentLayoutId===l.id ? '#c0392b' : '#1a1410',
              whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
              {currentLayoutId===l.id ? '● ' : ''}{l.name}
            </div>
            <div style={{fontSize:'0.62rem',color:'#9a8878',fontFamily:'monospace',marginTop:2}}>
              {l.top_wall}×{l.left_wall}" room · {l.tile_length}×{l.tile_width}" tile
            </div>
          </div>
          <button
            onClick={e=>{e.stopPropagation();handleDelete(l.id);}}
            style={{background:'none',border:'1px solid #e8c8c8',borderRadius:4,
              padding:'4px 8px',cursor:'pointer',color:'#c0392b',fontSize:'0.65rem',
              fontFamily:'monospace',flexShrink:0}}>
            🗑
          </button>
        </div>
      ))}
    </div>
  );

  const SaveStatusBadge = () => {
    if (!saveStatus) return null;
    const cfg = {
      saving: { bg:'#e67e22', text:'Saving…' },
      saved:  { bg:'#27ae60', text:'✓ Saved' },
      error:  { bg:'#c0392b', text:'✗ Error' },
    }[saveStatus];
    return (
      <div style={{position:'fixed',bottom:24,right:24,zIndex:80,
        background:cfg.bg,color:'#fff',fontFamily:'monospace',fontWeight:700,
        fontSize:'0.78rem',padding:'8px 16px',borderRadius:6,
        boxShadow:'3px 3px 0 rgba(0,0,0,0.2)'}}>
        {cfg.text}
      </div>
    );
  };

  // ── Desktop layout ──────────────────────────────────────────────────────────
  if (!isMobile) return (
    <>
    <Header />
    <div style={{minHeight:'100vh',background:'#f5f0e8',
      backgroundImage:'linear-gradient(#d4c8bc44 1px,transparent 1px),linear-gradient(90deg,#d4c8bc44 1px,transparent 1px)',
      backgroundSize:'36px 36px',padding:'40px 20px 80px',fontFamily:'monospace'}}>
      <div style={{maxWidth:900,margin:'0 auto'}}>
        <div style={{marginBottom:40,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
          <div style={{width:52,height:52,background:'#1a1410',display:'grid',gridTemplateColumns:'1fr 1fr',gap:3,padding:8,borderRadius:5,flexShrink:0}}>
            {[0,1,2,3].map(i=><div key={i} style={{background:'#f5f0e8',borderRadius:1}}/>)}
          </div>
          <h1 style={{fontFamily:'Georgia, serif',fontSize:'clamp(1.8rem,5vw,2.8rem)',fontStyle:'italic',letterSpacing:'-0.02em',color:'#1a1410',lineHeight:1,flex:1}}>Tile Calculator</h1>
          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            <button onClick={()=>{setShowLayouts(v=>{if(!v)fetchLayouts();return!v;})}}
              style={{padding:'9px 16px',fontFamily:'monospace',fontSize:'0.78rem',fontWeight:700,
                border:'1.5px solid #d4c8bc',borderRadius:6,background: showLayouts?'#1a1410':'#fffdf8',
                color:showLayouts?'#f5f0e8':'#1a1410',cursor:'pointer',boxShadow:'2px 2px 0 #d4c8bc'}}>
               My Layouts
            </button>
            <button onClick={openSaveModal}
              style={{padding:'9px 16px',fontFamily:'monospace',fontSize:'0.78rem',fontWeight:700,
                border:'1.5px solid #a93226',borderRadius:6,background:'#c0392b',
                color:'#fff',cursor:'pointer',boxShadow:'2px 2px 0 #a93226'}}>
               {currentLayoutId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        {/* Desktop layouts panel */}
        {showLayouts && (
          <div style={{...S.card,marginBottom:20}}>
            <CardLabel> My Layouts</CardLabel>
            <LayoutsPanel/>
          </div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:20,marginBottom:20}}>
          {roomCard}
          {tileCard}
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:20,marginBottom:20}}>
          {controlsContent}
        </div>

        {bestLayoutCard && <div style={{marginBottom:20}}>{bestLayoutCard}</div>}

        {calc ? (
          <>
            <StatsRow layout={layout} tileL={calc.tileLIn} tileB={calc.tileBIn} wastePct={wastePct}/>
            <div style={{...S.card,marginBottom:20}}>
              <CardLabel>Layout Preview — drag to pan · hover for dimensions</CardLabel>
              {tilePreviewEl}
            </div>
            {analysisContent}
          </>
        ) : (
          <div style={{...S.card,textAlign:'center',padding:'48px 24px',color:'#9a8878',fontSize:'0.8rem'}}>
            Enter all 4 wall measurements and tile size to calculate
          </div>
        )}
      </div>
      {showSaveModal && <SaveModal/>}
      <SaveStatusBadge/>
    </div>
    </>
  );

  // ── Mobile layout ───────────────────────────────────────────────────────────
  return (
    <>
    <Header />
    <div style={{minHeight:'100vh',background:'#f5f0e8',
      backgroundImage:'linear-gradient(#d4c8bc44 1px,transparent 1px),linear-gradient(90deg,#d4c8bc44 1px,transparent 1px)',
      backgroundSize:'36px 36px',paddingBottom:100,fontFamily:'monospace'}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',
        borderBottom:'1.5px solid #d4c8bc',background:'#f5f0e8',position:'sticky',top:0,zIndex:10}}>
        <div style={{width:32,height:32,background:'#1a1410',display:'grid',gridTemplateColumns:'1fr 1fr',
          gap:2,padding:5,borderRadius:4,flexShrink:0}}>
          {[0,1,2,3].map(i=><div key={i} style={{background:'#f5f0e8',borderRadius:1}}/>)}
        </div>
        <h1 style={{fontFamily:'Georgia, serif',fontSize:'1.2rem',fontStyle:'italic',
          letterSpacing:'-0.02em',color:'#1a1410',lineHeight:1,margin:0,flex:1}}>Tile Calculator</h1>
        <button onClick={()=>{setShowLayouts(v=>{if(!v)fetchLayouts();return!v;});}}
          style={{padding:'7px 10px',fontFamily:'monospace',fontSize:'0.72rem',fontWeight:700,
            border:'1.5px solid #d4c8bc',borderRadius:5,background:showLayouts?'#1a1410':'#fffdf8',
            color:showLayouts?'#f5f0e8':'#1a1410',cursor:'pointer'}}>
          📂
        </button>
        <button onClick={openSaveModal}
          style={{padding:'7px 10px',fontFamily:'monospace',fontSize:'0.72rem',fontWeight:700,
            border:'1.5px solid #a93226',borderRadius:5,background:'#c0392b',
            color:'#fff',cursor:'pointer'}}>
          💾
        </button>
      </div>

      <div style={{padding:'16px 16px 0'}}>
        {/* Room inputs */}
        <div style={{...S.card,marginBottom:12}}>
          <CardLabel>Room — all 4 walls</CardLabel>
          <div style={{marginBottom:10}}>
            <label style={S.label}>Unit</label>
            <select value={roomUnit} onChange={e=>setRoomUnit(e.target.value)} style={{...S.select,marginTop:4}}>
              {UNIT_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <FracInput label="Top"    value={topWall}    onChange={setTopWall}    placeholder="e.g. 216"/>
            <FracInput label="Bottom" value={bottomWall} onChange={setBottomWall} placeholder="e.g. 216"/>
            <FracInput label="Left"   value={leftWall}   onChange={setLeftWall}   placeholder="e.g. 132"/>
            <FracInput label="Right"  value={rightWall}  onChange={setRightWall}  placeholder="e.g. 132"/>
          </div>
          {calc && <div style={{marginTop:8,fontSize:'0.7rem',color:'#5a4e46',background:'#f5f0e8',
            padding:'6px 10px',borderRadius:4,border:'1px solid #d4c8bc'}}>
            Area ≈ <strong>{calc.areaSqFt} sq ft</strong>
          </div>}
        </div>

        {/* Tile inputs */}
        <div style={{...S.card,marginBottom:12}}>
          <CardLabel>Tile</CardLabel>
          <div style={{marginBottom:10}}>
            <label style={S.label}>Unit</label>
            <select value={tileUnit} onChange={e=>setTileUnit(e.target.value)} style={{...S.select,marginTop:4}}>
              {UNIT_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <FracInput label="Length" value={tileL} onChange={setTileL} placeholder="e.g. 15"/>
            <FracInput label="Width"  value={tileB} onChange={setTileB} placeholder="e.g. 15"/>
          </div>
          {calc && <div style={{marginTop:8,fontSize:'0.7rem',color:'#5a4e46',background:'#f5f0e8',
            padding:'6px 10px',borderRadius:4,border:'1px solid #d4c8bc'}}>
            Tile area: <strong>{calc.tileAreaSqFt} sq ft</strong>
          </div>}
        </div>

        {/* Sheet toggle buttons */}
        <div style={{display:'flex',gap:10,marginBottom:12}}>
          {[['settings','⚙ Settings'],['analysis','📊 Analysis']].map(([name, label]) => (
            <button key={name} onClick={()=>toggleSheet(name)}
              style={{flex:1,padding:'11px 8px',fontFamily:'monospace',fontSize:'0.78rem',
                fontWeight:700,borderRadius:6,cursor:'pointer',
                border:`1.5px solid ${sheet===name?'#c0392b':'#d4c8bc'}`,
                background:sheet===name?'#c0392b':'#fffdf8',
                color:sheet===name?'#fff':'#1a1410',
                boxShadow:sheet===name?'2px 2px 0 #a93226':'2px 2px 0 #d4c8bc'}}>
              {label}
            </button>
          ))}
        </div>

        {/* Preview */}
        {calc ? (
          <div style={{...S.card,marginBottom:12}}>
            <CardLabel>Preview — drag to pan</CardLabel>
            {tilePreviewEl}
          </div>
        ) : (
          <div style={{...S.card,textAlign:'center',padding:'32px 16px',color:'#9a8878',fontSize:'0.78rem'}}>
            Enter all 4 wall measurements and tile size to calculate
          </div>
        )}
      </div>

      {/* Settings side drawer */}
      <SideDrawer open={sheet==='settings'} onClose={()=>setSheet(null)} title="⚙ Settings">
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {controlsContent}
          {bestLayoutCard}
        </div>
      </SideDrawer>

      {/* Layouts bottom sheet */}
      <BottomSheet open={showLayouts} onClose={()=>setShowLayouts(false)} title="📂 My Layouts">
        <LayoutsPanel asSheet/>
      </BottomSheet>

      {/* Analysis bottom sheet */}
      <BottomSheet open={sheet==='analysis'} onClose={()=>setSheet(null)} title="📊 Analysis">
        {calc ? (
          <>
            <MobileStatsRow/>
            {analysisContent}
          </>
        ) : (
          <div style={{textAlign:'center',padding:'32px 16px',color:'#9a8878',fontSize:'0.78rem',fontFamily:'monospace'}}>
            Enter dimensions first to see analysis.
          </div>
        )}
      </BottomSheet>

      {showSaveModal && <SaveModal/>}
      <SaveStatusBadge/>
    </div>
    </>
  );
}